import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import type { PaginationRequiredQueryInterface } from '@zaoblako/shared/common/dtos/pagination-required-query.dto';
import type { HistoryFindInterface } from '@zaoblako/shared/common/dtos/history/history-find.dto';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { ButtonShowAndHideComponent } from '@zaoblako/web/shared/components/button-show-and-hide/button-show-and-hide.component';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { UserAvatarComponent } from '@zaoblako/web/shared/components/user-avatar/user-avatar.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TimelineModule } from 'primeng/timeline';

@Component({
  standalone: true,
  selector: 'crm-user-group-history',
  templateUrl: './user-group-history.component.html',
  imports: [
    CommonModule,
    CardModule,
    ButtonShowAndHideComponent,
    TimelineModule,
    OrderByPipe,
    UserAvatarComponent,
    ProgressSpinnerModule,
    SpinnerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserGroupHistoryComponent {
  @Input() public readonly index: number;
  public historyInfo: {
    items: HistoryFindInterface[];
    queryParams: PaginationRequiredQueryInterface;
    count: number;
    loading: boolean;
    paginationLoading: boolean;
    isShow: boolean;
  } = {
    items: [],
    queryParams: {
      limit: 100,
      offset: 0
    },
    count: 0,
    loading: true,
    paginationLoading: false,
    isShow: false
  };

  public constructor(
    private readonly userGroupService: UserGroupService,
    private readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public loadHistory(queryParams: PaginationRequiredQueryInterface) {
    this.historyInfo = {
      ...this.historyInfo,
      queryParams,
      loading: true
    };

    this.userGroupService
      .getHistory(this.formStoreService.getState.forms[this.index]?.entityId, this.historyInfo.queryParams)
      .subscribe({
        next: (history) => {
          this.historyInfo = {
            ...this.historyInfo,
            count: history.count,
            items: history.items,
            loading: false
          };
          this.cdRef.markForCheck();
        }
      });
  }

  public toHistory(history: any): HistoryFindInterface {
    return history as HistoryFindInterface;
  }

  public onScrollEndHistory(target: HTMLElement) {
    if (target.offsetHeight + target.scrollTop >= target.scrollHeight) {
      if (
        this.historyInfo?.items?.length < this.historyInfo?.count &&
        !this.historyInfo?.paginationLoading &&
        !this.historyInfo?.loading
      ) {
        this.historyInfo = {
          ...this.historyInfo,
          queryParams: {
            ...this.historyInfo.queryParams,
            offset: this.historyInfo.queryParams.offset + this.historyInfo.queryParams.limit
          },
          paginationLoading: true
        };

        this.userGroupService
          .getHistory(this.formStoreService.getState.forms[this.index]?.entityId, this.historyInfo.queryParams)
          .subscribe({
            next: (history) => {
              this.historyInfo = {
                ...this.historyInfo,
                count: history.count,
                items: this.historyInfo.items.concat(history.items),
                paginationLoading: false
              };
              this.cdRef.markForCheck();
            }
          });
      }
    }
  }

  public showHistoryChange(showHistory: boolean) {
    if (showHistory) {
      this.loadHistory({
        ...this.historyInfo.queryParams,
        offset: 0
      });
    }
    this.historyInfo = {
      ...this.historyInfo,
      isShow: !this.historyInfo.isShow
    };
  }
}
